import React, { useEffect, useMemo, useState } from 'react';

type CallStatus = { success?: boolean; message?: string; [k: string]: any } | null;
type FlowResults = {
  originPath?: string;
  donor?: { name?: string; email?: string; amount?: string; donationType?: string };
  payment?: CallStatus;
  addDonation?: CallStatus;
  email?: (CallStatus & { receiptUrl?: string }) | null;
} | null;

const Success: React.FC = () => {
  const [results, setResults] = useState<FlowResults>(null);
  // Use brand-specific external assets (provided)
  const DEFAULT_LOGO = '';
  const RMS_LOGO = 'https://www.radhamadhavsociety.org/assets/images/about/i-logo-2.png';
  const HYC_LOGO = 'https://harmonyyouthcenter.org/Content/images/footer.png';
  const [brandLogoSrc, setBrandLogoSrc] = useState<string>('');
  const [logoError, setLogoError] = useState<boolean>(false);

  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.title = 'Donation Success';
    }
    if (typeof window !== 'undefined') {
      try {
        const raw = window.sessionStorage.getItem('donationFlowResults');
        if (raw) setResults(JSON.parse(raw));
      } catch {}
    }
  }, []);

  const allSucceeded = !!(
    results?.payment?.success &&
    results?.addDonation?.success &&
    ((typeof (results as any)?.email === 'string' && !!(results as any)?.email) || (results as any)?.email?.success)
  );
  const statusStyle = (ok?: boolean): React.CSSProperties => (
    ok
      ? { background: '#d1e7dd', color: '#0f5132', border: '1px solid #badbcc', padding: '12px 16px', borderRadius: 8 }
      : { background: '#f8d7da', color: '#842029', border: '1px solid #f5c2c7', padding: '12px 16px', borderRadius: 8 }
  );

  const goBack = () => {
    const origin = (results as any)?.originPath;
    if (typeof window !== 'undefined') {
      window.location.href = typeof origin === 'string' && origin ? origin : '/';
    }
  };

  const receiptUrl = useMemo(() => {
    const e: any = results?.email || {};
    if (typeof e === 'string') return e;
    return (
      e?.receiptUrl ||
      e?.ReceiptUrl ||
      e?.receiptURL ||
      e?.Response ||
      e?.response ||
      e?.data?.receiptUrl ||
      e?.data?.ReceiptUrl ||
      e?.data?.receiptURL ||
      e?.data?.Response ||
      e?.data?.response ||
      ''
    );
  }, [results]);

  const paymentKnown = typeof results?.payment?.success === 'boolean';
  const paymentOk = results?.payment?.success === true;
  const headerText = paymentKnown ? (paymentOk ? 'Payment Successful' : 'Payment Failed') : 'Submission Results';

  const emailOk = useMemo(() => {
    const e: any = (results as any)?.email;
    if (typeof e === 'string') return !!e; // raw URL implies success
    return !!e?.success;
  }, [results]);

  const emailMessage = useMemo(() => {
    const e: any = (results as any)?.email;
    if (typeof e === 'string') return 'A confirmation email was sent.';
    return e?.message || (emailOk ? 'A confirmation email was sent.' : 'Failed to send confirmation email.');
  }, [results, emailOk]);

  // Pick brand based on origin path
  useEffect(() => {
    const origin = (results as any)?.originPath?.toLowerCase?.() || '';
    setLogoError(false);
    if (/^\/hyc(\/|$)/.test(origin)) {
      setBrandLogoSrc(HYC_LOGO);
    } else if (/^\/rms(\/|$)/.test(origin)) {
      setBrandLogoSrc(RMS_LOGO);
    } else {
      setBrandLogoSrc(DEFAULT_LOGO);
    }
  }, [results]);

  const brandAlt = useMemo(() => {
    const origin = (results as any)?.originPath?.toLowerCase?.() || '';
    if (/^\/hyc(\/|$)/.test(origin)) return 'Harmony Youth Center';
    if (/^\/rms(\/|$)/.test(origin)) return 'Radha Madhav Society';
    return 'Yugal Kunj';
  }, [results]);

  // Set document title with brand context
  useEffect(() => {
    if (typeof document !== 'undefined') {
      const title = brandAlt ? `${brandAlt} Donation - Success` : 'Donation Success';
      document.title = title;
    }
  }, [brandAlt]);

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '16px',
      background: 'linear-gradient(135deg, #ffffff 0%, #f0f4ff 100%)',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
    }}>
      <div style={{
        width: '100%',
        maxWidth: 550,
        borderRadius: '20px',
        background: '#ffffff',
        border: '1px solid rgba(99, 102, 241, 0.15)',
        boxShadow: '0 12px 40px rgba(99, 102, 241, 0.15)',
        padding: '36px 32px',
        textAlign: 'center',
        animation: 'fadeIn 0.6s ease-in-out'
      }}>
        {/* Top Accent Bar */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '4px',
          background: 'linear-gradient(90deg, #6366f1 0%, #10b981 50%, #f59e0b 100%)',
          borderRadius: '20px 20px 0 0'
        }} />

        {/* Logo */}
        {!logoError && brandLogoSrc ? (
          <img
            src={brandLogoSrc}
            alt={brandAlt}
            style={{ width: 90, height: 'auto', marginBottom: 16, opacity: 1 }}
            onError={() => setLogoError(true)}
          />
        ) : null}

        {/* Success Icon */}
        {paymentOk && (
          <div style={{
            position: 'relative',
            width: 120,
            height: 120,
            margin: '0 auto 24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            {/* Outer glow circle */}
            <div style={{
              position: 'absolute',
              width: '120px',
              height: '120px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(16, 185, 129, 0.1) 100%)',
              border: '2px solid rgba(16, 185, 129, 0.3)',
              boxShadow: '0 0 50px rgba(16, 185, 129, 0.25), inset 0 0 30px rgba(16, 185, 129, 0.1)',
              animation: 'pulse 2s ease-in-out infinite',
            }} />
            {/* Main circle */}
            <div style={{
              position: 'relative',
              width: 100,
              height: 100,
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '56px',
              color: '#ffffff',
              boxShadow: '0 12px 40px rgba(16, 185, 129, 0.4), 0 0 0 4px rgba(16, 185, 129, 0.1)',
              fontWeight: 800,
              zIndex: 1,
            }}>
              ✓
            </div>
          </div>
        )}

        {/* Header */}
        <h1 style={{
          fontSize: '2rem',
          margin: '0 0 8px 0',
          fontWeight: 800,
          color: paymentKnown ? (paymentOk ? '#059669' : '#dc2626') : '#1f2937',
          letterSpacing: '-0.02em'
        }}>
          {headerText}
        </h1>

        {/* Success/Failure Message */}
        {allSucceeded ? (
          <p style={{
            marginTop: 12,
            marginBottom: 0,
            fontSize: '0.95rem',
            color: '#059669',
            textAlign: 'center',
            background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(16, 185, 129, 0.05) 100%)',
            border: '1px solid rgba(16, 185, 129, 0.3)',
            borderRadius: 12,
            padding: '12px 16px',
            fontWeight: 600
          }}>
            🎉 Thank you for your generous donation!
          </p>
        ) : paymentKnown && !paymentOk ? (
          <p style={{
            marginTop: 12,
            marginBottom: 0,
            fontSize: '0.95rem',
            color: '#991b1b',
            textAlign: 'center',
            background: 'linear-gradient(135deg, rgba(220, 38, 38, 0.1) 0%, rgba(220, 38, 38, 0.05) 100%)',
            border: '1px solid rgba(220, 38, 38, 0.3)',
            borderRadius: 12,
            padding: '12px 16px',
            fontWeight: 600
          }}>
            ⚠️ Your payment could not be processed. Please review the details below and try again.
          </p>
        ) : (
          <p style={{ marginTop: 12, marginBottom: 0, color: '#666', fontSize: '0.95rem' }}>
            Below are the results of your submission.
          </p>
        )}

        {/* No Results */}
        {!results && (
          <div style={{
            marginTop: 24,
            background: 'linear-gradient(135deg, rgba(220, 38, 38, 0.1) 0%, rgba(220, 38, 38, 0.05) 100%)',
            border: '1px solid rgba(220, 38, 38, 0.3)',
            borderRadius: 12,
            padding: '20px',
            color: '#991b1b'
          }}>
            <div style={{ fontWeight: 700, marginBottom: 8, fontSize: '1.05rem' }}>No Results Found</div>
            <div style={{ fontSize: '0.95rem', marginBottom: 16 }}>We couldn't find any recent submission results. Please complete the donation form first.</div>
            <button type="button" className="btn btn-primary" onClick={goBack} style={{
              background: 'linear-gradient(135deg, #6366f1 0%, #ec4899 100%)',
              border: 'none',
              borderRadius: 8,
              padding: '10px 24px',
              fontWeight: 600,
              color: '#fff',
              cursor: 'pointer',
              transition: 'all 200ms ease'
            }}>
              Go Back
            </button>
          </div>
        )}

        {/* Donor Info */}
        {results?.donor?.name && (
          <div style={{
            marginTop: 18,
            padding: '12px 16px',
            background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.08) 0%, rgba(99, 102, 241, 0.04) 100%)',
            borderRadius: 12,
            border: '1px solid rgba(99, 102, 241, 0.2)',
            fontSize: '0.9rem',
            color: '#1f2937'
          }}>
            <div style={{ marginBottom: 4 }}>
              <span style={{ fontWeight: 600 }}>Donor:</span> {results.donor.name}
            </div>
            {results?.donor?.email && (
              <div style={{ marginBottom: 4 }}>
                <span style={{ fontWeight: 600 }}>Email:</span> {results.donor.email}
              </div>
            )}
            {results?.donor?.amount && (
              <div style={{ marginBottom: 4 }}>
                <span style={{ fontWeight: 600 }}>Amount:</span> ${results.donor.amount}
              </div>
            )}
            {results?.donor?.donationType && (
              <div>
                <span style={{ fontWeight: 600 }}>Type:</span> {results.donor.donationType}
              </div>
            )}
          </div>
        )}

        {/* Status Cards */}
        {results && (
          <div style={{ marginTop: 18 }}>
            {/* Payment */}
            <div style={{
              marginBottom: 12,
              padding: '14px 16px',
              background: results?.payment?.success 
                ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(16, 185, 129, 0.05) 100%)'
                : 'linear-gradient(135deg, rgba(220, 38, 38, 0.1) 0%, rgba(220, 38, 38, 0.05) 100%)',
              border: results?.payment?.success 
                ? '1px solid rgba(16, 185, 129, 0.3)'
                : '1px solid rgba(220, 38, 38, 0.3)',
              borderRadius: 10,
              color: results?.payment?.success ? '#059669' : '#991b1b'
            }}>
              <div style={{ fontWeight: 700, marginBottom: 6, fontSize: '0.95rem' }}>
                {results?.payment?.success ? '✓ Payment' : '✗ Payment Failed'}
              </div>
              <div style={{ fontSize: '0.85rem', lineHeight: '1.4' }}>
                <strong>Reason:</strong> {results?.payment?.message || (results?.payment?.success ? 'Payment processed successfully.' : 'Payment could not be processed. Please check your payment details and try again.')}
              </div>
            </div>

            {/* Donation Record */}
            <div style={{
              marginBottom: 12,
              padding: '14px 16px',
              background: results?.addDonation?.success 
                ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(16, 185, 129, 0.05) 100%)'
                : 'linear-gradient(135deg, rgba(220, 38, 38, 0.1) 0%, rgba(220, 38, 38, 0.05) 100%)',
              border: results?.addDonation?.success 
                ? '1px solid rgba(16, 185, 129, 0.3)'
                : '1px solid rgba(220, 38, 38, 0.3)',
              borderRadius: 10,
              color: results?.addDonation?.success ? '#059669' : '#991b1b'
            }}>
              <div style={{ fontWeight: 700, marginBottom: 6, fontSize: '0.95rem' }}>
                {results?.addDonation?.success ? '✓ Donation Record' : '✗ Donation Record Failed'}
              </div>
              <div style={{ fontSize: '0.85rem', lineHeight: '1.4' }}>
                <strong>Reason:</strong> {results?.addDonation?.message || (results?.addDonation?.success ? 'Donation recorded successfully.' : 'Unable to record donation. Please contact support if this issue persists.')}
              </div>
            </div>

            {/* Email Receipt */}
            <div style={{
              padding: '14px 16px',
              background: emailOk 
                ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(16, 185, 129, 0.05) 100%)'
                : 'linear-gradient(135deg, rgba(220, 38, 38, 0.1) 0%, rgba(220, 38, 38, 0.05) 100%)',
              border: emailOk 
                ? '1px solid rgba(16, 185, 129, 0.3)'
                : '1px solid rgba(220, 38, 38, 0.3)',
              borderRadius: 10,
              color: emailOk ? '#059669' : '#991b1b'
            }}>
              <div style={{ fontWeight: 700, marginBottom: 6, fontSize: '0.95rem' }}>
                {emailOk ? '✓ Email Receipt' : '✗ Email Receipt Failed'}
              </div>
              <div style={{ fontSize: '0.85rem', lineHeight: '1.4', marginBottom: receiptUrl ? 8 : 0 }}>
                <strong>Reason:</strong> {emailMessage}
              </div>
              {receiptUrl && (
                <a href={receiptUrl} target="_blank" rel="noopener noreferrer" style={{
                  display: 'inline-block',
                  marginTop: 8,
                  padding: '6px 12px',
                  background: 'linear-gradient(135deg, #6366f1 0%, #ec4899 100%)',
                  color: '#fff',
                  textDecoration: 'none',
                  borderRadius: 6,
                  fontWeight: 600,
                  fontSize: '0.8rem',
                  transition: 'all 200ms ease'
                }}>
                  📥 Download Receipt
                </a>
              )}
            </div>
          </div>
        )}

        {/* Action Button */}
        <div style={{ marginTop: 20 }}>
          <button type="button" onClick={goBack} style={{
            width: '100%',
            padding: '14px 24px',
            background: 'linear-gradient(135deg, #6366f1 0%, #ec4899 100%)',
            color: '#fff',
            border: 'none',
            borderRadius: 10,
            fontSize: '1rem',
            fontWeight: 700,
            cursor: 'pointer',
            transition: 'all 200ms ease',
            boxShadow: '0 6px 20px rgba(99, 102, 241, 0.3)'
          }} onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 10px 28px rgba(99, 102, 241, 0.4)';
          }} onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 6px 20px rgba(99, 102, 241, 0.3)';
          }}>
            Return to Donation
          </button>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes pulse {
          0%, 100% {
            transform: scale(1);
            opacity: 1;
          }
          50% {
            transform: scale(1.1);
            opacity: 0.8;
          }
        }
      `}</style>
    </div>
  );
};

export default Success;
